import { Card, CardContent, Image, View } from "@/components/ui";
import { Grid } from "@/components/shared/grid";
import { Typography } from "@/components/shared/typography";
import { ExpandableText } from "@/components/shared/typography/expandable-text";
import { Ratings } from "@/components/ui/ratings";
import { formatDate } from "@/lib/helpers/date";
import type { ClientReviewData } from "@/types/review";
import { cn } from "@/lib/utils";

type ReviewCardProps = Pick<
  ClientReviewData,
  "clientName" | "rating" | "date"
> & {
  ltv?: number;
  service?: string;
  description?: string | null;
  className?: string;
  images?: string[];
  onClick?: (imageIndex: number) => void;
  inverseTextColor?: boolean;
};

const textColor = (inverse: boolean) =>
  inverse ? ("white" as const) : ("foreground-intermediate" as const);

export function ReviewCard({
  clientName,
  rating,
  date,
  ltv,
  service,
  description,
  className,
  images,
  onClick,
  inverseTextColor = false,
}: ReviewCardProps) {
  const color = textColor(inverseTextColor);
  return (
    <Card
      gap="none"
      radius="lg"
      className={cn("border border-border", className)}
    >
      <CardContent className="flex flex-col gap-2 p-4">
        <View className="flex flex-row items-center gap-1 p-0">
          <Typography variant="body-sm" color={color}>
            {clientName}
          </Typography>
          {ltv && (
            <>
              <Typography variant="caption-md" color={color}>
                ・
              </Typography>
              <Typography variant="caption-md" color="success">
                {ltv} lifetime bookings
              </Typography>
            </>
          )}
        </View>
        <View className="flex flex-row justify-start gap-1 p-0">
          <Ratings rating={rating} disabled={true} size={16} variant="yellow" />
          <Typography variant="caption-md" color={color}>
            ・
          </Typography>
          <Typography variant="caption-md" color={color}>
            {formatDate(date, "MMM d, yyyy")}
          </Typography>
        </View>
        {service && service !== "" && (
          <Typography variant="caption-md" color={color}>
            Service Booked: {service}
          </Typography>
        )}
        {description && description !== "" && (
          <ExpandableText variant="body-sm" color={color}>
            {description}
          </ExpandableText>
        )}
        {images && images.length > 0 && (
          <View className="mt-2 w-full p-0">
            <Grid
              columns={3}
              gap={4}
              data={images.map((image, index) => ({ image, index }))}
              render={({ image, index }) => (
                <View
                  className="aspect-square overflow-hidden rounded-md"
                  onClick={() => onClick?.(index)}
                >
                  <Image
                    src={image}
                    alt="Review Image"
                    contentFit="cover"
                    className="h-full w-full object-cover"
                  />
                </View>
              )}
            />
          </View>
        )}
      </CardContent>
    </Card>
  );
}
